import { getQueriesFromSelectedFilters } from '~/utils/product-filter/filter-helpers'
import { QUERY_KEY_ORDER } from './constants'

export default function useProductListingRoutes() {
  const route = useRoute()
  const router = useRouter()
  const filterStore = useProductListingFiltersStore()
  const { selectedFilterParams } = filterStore
  const sortStore = useProductListingSortStore()
  const { DEFAULT_SORT } = sortStore

  async function updateProductListingRoute(
    toUpdate: {
      [key: string]: boolean
    } = {}
  ) {
    // Category params
    const paramsObj = { ...route.params }

    if (toUpdate.categoryParams) _updateCatParamsWithState(paramsObj)

    // Filter queries
    const queryObj = getAllQueriesFromRoute()

    if (toUpdate.filterQuery) _updateFilterQueryWithState(queryObj)
    if (toUpdate.withoutTypeFilters) _removeTypeFiltersFromQuery(queryObj)

    // Sort queries
    if (toUpdate.sortQuery) _updateSortQueryWithState(queryObj)

    _cleanQueries(queryObj)

    await router.push({
      params: paramsObj,
      query: queryObj,
    })
  }

  function getCategoryPathFromRoute() {
    const { topCategory, category, subCategory } = route.params
    return [topCategory, category, subCategory].filter(Boolean).join('/')
  }

  // TypeFilters are ordered last
  // Verbose naming to differentiate between getting queries from state
  function getAllQueriesFromRoute() {
    return {
      ..._getOrderedQueriesFromRoute(),
      ..._getTypeFiltersQueriesFromRoute(),
    }
  }

  /**
   * - - - - - Internal Functions - - - - -
   */
  function _getOrderedQueriesFromRoute() {
    const queries: { [key: string]: string | undefined } = {}

    QUERY_KEY_ORDER.forEach(key => {
      queries[key] = route.query[key]?.toString()
    })
    return queries
  }

  function _getTypeFiltersQueriesFromRoute() {
    const queries: { [key: string]: string | undefined } = {}

    Object.keys(route.query).forEach(key => {
      if (!QUERY_KEY_ORDER.includes(key)) {
        queries[key] = route.query[key]?.toString()
      }
    })
    return queries
  }

  function _updateCatParamsWithState(paramsObj: {
    [key: string]: string | string[]
  }): void {
    const newCatParams = filterStore.selectedCategory.split('/')
    paramsObj.topCategory = newCatParams[0] || ''
    paramsObj.category = newCatParams[1] || ''
    paramsObj.subCategory = newCatParams[2] || ''
  }

  function _updateFilterQueryWithState(queryObj: {
    [key: string]: string | undefined
  }): void {
    Object.assign(queryObj, getQueriesFromSelectedFilters(selectedFilterParams))
  }

  function _removeTypeFiltersFromQuery(queryObj: {
    [key: string]: string | undefined
  }): void {
    Object.keys(queryObj).forEach(key => {
      if (!QUERY_KEY_ORDER.includes(key)) {
        // Eslint: Do not delete dynamically computed keys; thus undefined is assigned
        queryObj[key] = undefined
      }
    })
  }

  function _updateSortQueryWithState(queryObj: {
    [key: string]: string | undefined
  }): void {
    const newSortQuery =
      toValue(sortStore.selectedSortOption) === DEFAULT_SORT
        ? undefined
        : toValue(sortStore.selectedSortOption)

    Object.assign(queryObj, { sort: newSortQuery })
  }

  // Removes queries that are not in state structure(selectedFilterParams)
  function _cleanQueries(queryObj: {
    [key: string]: string | undefined
  }): void {
    const keysToKeep = [
      ...QUERY_KEY_ORDER,
      ...Object.keys(selectedFilterParams.types),
    ]

    Object.keys(queryObj).forEach(key => {
      if (!keysToKeep.includes(key)) {
        queryObj[key] = undefined
      }
    })
  }

  return {
    updateProductListingRoute,
    getCategoryPathFromRoute,
    getAllQueriesFromRoute,
  }
}
